/**
 * User Migration Modal Component
 *
 * Modal for creating or editing migration users with form validation.
 * Supports both create and edit modes.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { MigrationUser } from '@/types/user-migration';
import { AlertCircle } from 'lucide-react';

/**
 * Form Schema for Create
 */
const createFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().max(15, 'Phone must be 15 characters or less').optional(),
});

/**
 * Form Schema for Edit
 */
const editFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().max(15, 'Phone must be 15 characters or less').optional(),
});

type CreateFormValues = z.infer<typeof createFormSchema>;
type EditFormValues = z.infer<typeof editFormSchema>;

/**
 * User Migration Modal Props
 */
interface UserMigrationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** User data for edit mode (null for create mode) */
  user: MigrationUser | null;
  /** Submit callback */
  onSubmit: (data: CreateFormValues | EditFormValues) => Promise<void>;
  /** Whether the form is submitting */
  isSubmitting: boolean;
  /** Error message */
  error?: string | null;
}

/**
 * User Migration Modal Component
 */
export function UserMigrationModal({
  open,
  onClose,
  user,
  onSubmit,
  isSubmitting,
  error,
}: UserMigrationModalProps) {
  const isEditMode = user !== null;

  // Initialize form with appropriate schema
  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
    },
  });

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      email: user?.email || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
    },
  });

  const form = isEditMode ? editForm : createForm;

  // Reset form when modal opens or user changes
  useEffect(() => {
    if (open && user) {
      editForm.reset({
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    } else if (open && !user) {
      createForm.reset({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
      });
    }
  }, [open, user, createForm, editForm]);

  /**
   * Handle Form Submit
   */
  const handleSubmit = async (data: CreateFormValues | EditFormValues) => {
    await onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Migration User' : 'Create Migration User'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the migration user information below.'
              : 'Add a new user to the migration list.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              {...(isEditMode ? editForm.register('email') : createForm.register('email'))}
              placeholder="user@example.com"
              disabled={isSubmitting}
            />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              {...(isEditMode ? editForm.register('firstName') : createForm.register('firstName'))}
              placeholder="Enter first name"
              disabled={isSubmitting}
            />
            {form.formState.errors.firstName && (
              <p className="text-sm text-destructive">{form.formState.errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              {...(isEditMode ? editForm.register('lastName') : createForm.register('lastName'))}
              placeholder="Enter last name"
              disabled={isSubmitting}
            />
            {form.formState.errors.lastName && (
              <p className="text-sm text-destructive">{form.formState.errors.lastName.message}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              {...(isEditMode ? editForm.register('phone') : createForm.register('phone'))}
              placeholder="Enter phone number"
              disabled={isSubmitting}
              maxLength={15}
            />
            {form.formState.errors.phone && (
              <p className="text-sm text-destructive">{form.formState.errors.phone.message}</p>
            )}
          </div>

          {/* Dialog Footer */}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{isEditMode ? 'Update User' : 'Create User'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
